import styled from "styled-components";
import { LinkButton } from "../../../components/controls/linkButton";
import { DetailsItem } from "../../../components/details/detailsItem";
import { MiniHeader } from "../../../components/details/miniHeader";
import { encode } from "../../../lib/base64";
import { IBatteryModel } from "./deviceDetailsInformation";
import { useWorldRoutes } from "../../../routes/parts/allWorldRoutes";
import moment from "moment";
import { useTranslation } from "@lib/useTypedTranslation";

import { TTypedTFunction } from "@lib/useTypedTranslation";
import { RoundedLabel } from "../../../components/labels/roundedLabel";

const ViewInBatteries = styled(LinkButton)`
  display: inline-block;
  margin-top: 2rem;
`;
ViewInBatteries.displayName = 'ViewInBatteries';

export const getStatusLabel = (index: number, data: any, t: TTypedTFunction) => {
  if (data.smartBatteries && data.smartBatteries[index]) {
    const battery = data.smartBatteries[index];
    const labels = [];

    if (battery.healthStatus === 'red') {
      labels.push(<RoundedLabel key='health' title={t('POOR_HEALTH')} icon="fas fa-times-circle" type="red" />);
    }

    if (battery.averageDischargeStatus === 'red') {
      labels.push(<RoundedLabel key='averageDischarge' title={t("HIGH_DISCHARGE", { ns: 'batteryEssentialsDeviceView' })} icon="fas fa-times-circle" type="red" />);
    }

    if (battery.cycleCountStatus === 'red') {
      labels.push(<RoundedLabel key='cycleCount' title={t('HIGH_CYCLE_COUNT')} icon="fas fa-times-circle" type="red" />);
    }

    if (battery.healthStatus === 'yellow') {
      labels.push(<RoundedLabel key='health' title={t('HEALTH_WARNING')} icon="fas fa-exclamation-triangle" type="yellow" />);
    }

    if (battery.averageDischargeStatus === 'yellow') {
      labels.push(<RoundedLabel key='averageDischarge' title={t("HIGH_DISCHARGE", { ns: 'batteryEssentialsDeviceView' })} icon="fas fa-exclamation-triangle" type="yellow" />);
    }

    if (battery.cycleCountStatus === 'yellow') {
      labels.push(<RoundedLabel key='cycleCount' title={t('CYCLE_COUNT_WARNING')} icon="fas fa-exclamation-triangle" type="yellow" />);
    }

    if (battery.batteryStatus === 'green' && labels.length === 0) {
      labels.push(<RoundedLabel title={t('NO_PROBLEMS')} icon="fas fa-check" type="green" />);
    }

    return labels.length === 0 ? undefined : labels.length === 1 ? labels[0] : <div>{labels}</div>;
  }
};

export const createBatteryModel = (index: number, data: any, t: TTypedTFunction): IBatteryModel => {
  const battery = data.smartBatteries ? data.smartBatteries[index] : undefined;
  return {
    id: battery?.id ?? undefined,
    batteryPartNumber: battery?.partNumber ?? undefined,
    batterySerialNumber: battery?.serialNumber ?? undefined,
    capacityFactor: battery?.capacityFactor ? `${Math.round(Number.parseFloat(battery.capacityFactor) * 100)}%` : undefined,
    cycleCount: battery ? battery.cycleCount : undefined,
    averageDischarge: battery?.averageDischarge ? `${battery.averageDischarge}%` : undefined,
    statusLabel: battery ? getStatusLabel(index, data, t) : undefined
  };
};

interface IProps {
  data: any,
  showViewInBatteries: boolean,
  areMultipleBatteries: boolean,
  index: number
}

export function LastKnownBatteryColumn(props: IProps) {
  const linkToBatteries = useWorldRoutes().batteryEssentials.battery;

  const ns = 'batteryEssentials';
  const ns2 = 'batteryEssentialsDeviceView';

  const { t } = useTranslation(['translation', ns, ns2]);

  const batteryModel = createBatteryModel(props.index, props.data, t);

  const batteryId = batteryModel?.id ?? '';
  const base64Id = encode(batteryId);

  return <div className="col-md-4 col-sm-12 col-12" key={batteryId} data-battery-index={props.index + ''}>
    <MiniHeader title={`${t('LAST_KNOWN_BATTERY', { ns: ns2 })}${props.areMultipleBatteries ? ' ' + (props.index + 1) : ''}`} icon="fas fa-battery-three-quarters" />

    <DetailsItem data={batteryModel} name={t('PART_NUMBER', { ns })} path='batteryPartNumber' />
    <DetailsItem data={batteryModel} name={t('SERIAL_NUMBER', { ns: 'translation' })} path='batterySerialNumber' />

    <DetailsItem data={batteryModel} name={t('HEALTH', { ns })} path='capacityFactor' />
    <DetailsItem data={batteryModel} name={t('CYCLE_COUNT', { ns })} path='cycleCount' />
    <DetailsItem data={batteryModel} name={t('AVERAGE_DISCHARGE_RATE', { ns: ns2 })} path='averageDischarge' />

    <DetailsItem data={batteryModel} name={t('STATUS', { ns: 'translation' })} path='statusLabel' />

    {!!props.showViewInBatteries && (
      <ViewInBatteries
        className='align-self-center'
        dataId="view-in-batteries"
        to={`${linkToBatteries}?id=${encodeURIComponent(base64Id)}&ts=${moment.utc().valueOf()}`}
        external={false}
      >
        {t('VIEW_IN_BATTERY_STATUS', { ns: ns2 })}
      </ViewInBatteries>
    )}
  </div>;
}
